import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronLeft, Layers } from "lucide-react";

import { Button } from "../../../components/Button";
import SuccessPopup from "../../../components/SuccessPopup";
import { useAuth } from "../../../components/contexts/AuthContext";
import { getData } from "../../../components/hooks/getData";
import { CampaignBriefingForm } from "./CampaignBriefingForm";
import { CAMPAIGN_PIECE_QUERY_KEY, CampaignProgressPanel } from "./CampaignProgressPanel";
import { estimatedMinutes, panelSx } from "./campaignUi";
import { useCampaignGeneration } from "./useCampaignGeneration";
import { useContentTutorial } from "../ContentTutorialContext";
import { CONTENT_TUTORIAL_MOCK_CONFIG_ID } from "../../../Tutorials/contentGenerator/contentTutorialMocks.js";
import { WorkflowStepper } from "../workflow/WorkflowStepper";
import { getPieceWorkflow } from "../workflow/workflowUtils";
import { ReadOnlyClientNotice } from "../ReadOnlyClientNotice";
import { isClientReadOnly } from "../../../utils/clientAccess";

const LIST_PATH = "/HerramientasIA/contenidos";

const briefingFromConfig = (config) => ({
  objective: "",
  topic: "",
  initialPrompt: "",
  tone: config?.tone || "formal",
  languageType: config?.language_type || "direct",
  language: config?.language || "es",
  frequency: 3,
  duration: 3,
});

const minutesLabel = (pieces) => {
  const minutes = estimatedMinutes(pieces);
  return `Tiempo estimado: ${minutes} ${minutes === 1 ? "minuto" : "minutos"}`;
};

/**
 * Content creation page — /HerramientasIA/contenidos/generar?config=<configId>[&tipo=individual].
 * Briefing on the left; on the right the topics to review and the generated pieces.
 * With tipo=individual it creates one piece from a topic (no rhythm, no topic proposal).
 */
export function CampaignPage() {
  const [searchParams] = useSearchParams();
  // Remount when switching client or type (e.g. "add piece" from a batch) so no generation state leaks.
  return <CreationPage key={`${searchParams.get("config")}-${searchParams.get("tipo")}`} />;
}

function CreationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { interceptorReady, user } = useAuth();
  const { setPageContext } = useOutletContext();
  const theme = useTheme();
  // Tutorial: a mock creation (single, config, briefing, phase, topics, pieces); nothing is fetched or generated.
  const tutorialCampaign = useContentTutorial().tutorialMock?.campaign ?? null;
  const configId = searchParams.get("config");
  const single = tutorialCampaign ? Boolean(tutorialCampaign.single) : searchParams.get("tipo") === "individual";

  const [popup, setPopup] = useState({ open: false, message: "", error: false });
  const notify = useCallback((message, error = false) => setPopup({ open: true, message, error }), []);

  // The tutorial's config only exists as mock data (never fetched, even right after the tutorial ends).
  const canFetchConfig = Boolean(configId) && configId !== CONTENT_TUTORIAL_MOCK_CONFIG_ID;
  const { data: fetchedConfig, isLoading: isLoadingConfig, isError: fetchedConfigError } = getData(
    canFetchConfig ? `content-generation/configs/${configId}/` : null,
    interceptorReady && canFetchConfig,
    configId
  );
  const config = tutorialCampaign ? tutorialCampaign.config : fetchedConfig;
  const configError = !tutorialCampaign && fetchedConfigError;

  const realCampaign = useCampaignGeneration({ notify });
  const campaign = tutorialCampaign
    ? {
        ...realCampaign,
        phase: tutorialCampaign.phase,
        topics: tutorialCampaign.topics,
        progress: tutorialCampaign.progress,
        pieceIds: tutorialCampaign.pieces.map((piece) => piece.id),
        isStarting: false,
        isConfirming: false,
        setTopic: () => {},
        removeTopic: () => {},
        addTopic: () => {},
        confirmTopics: () => {},
      }
    : realCampaign;
  // A single piece has no topic proposal: its "starting" moment is already generation.
  const phase = single && campaign.phase === "topics_loading" ? "generating" : campaign.phase;
  const started = phase !== "briefing";
  const [stateBriefing, setBriefing] = useState(() => briefingFromConfig(null));
  const briefing = tutorialCampaign ? tutorialCampaign.briefing : stateBriefing;
  const [initializedFor, setInitializedFor] = useState(null);

  // Prefill the briefing from the client config once it loads.
  useEffect(() => {
    if (!config || initializedFor === config.id) return;
    setInitializedFor(config.id);
    setBriefing(briefingFromConfig(config));
  }, [config, initializedFor]);

  const goToList = useCallback(() => navigate(LIST_PATH), [navigate]);

  // Same "Tipo: nombre" title as the other content pages (e.g. Contenido: …).
  useEffect(() => {
    const pageLabel = single ? "Nueva pieza de contenido individual" : "Nueva campaña";
    setPageContext({
      title: (
        <>
          Contenidos:{"  "}
          <span style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}>{pageLabel}</span>
        </>
      ),
      showTitle: true,
      leftContent: (
        <IconButton
          onClick={goToList}
          aria-label="Volver a contenidos"
          size="small"
          sx={{ color: "primary.main", padding: "4px", "&:hover": { backgroundColor: "action.hover" } }}
        >
          <ChevronLeft size={20} />
        </IconButton>
      ),
      titleRightContent: null,
      rightContent: null,
      breadcrumbItems: [
        { label: "Contenidos", onClick: goToList },
        { label: `${pageLabel}${config?.client_name ? ` · ${config.client_name}` : ""}` },
      ],
    });
  }, [setPageContext, goToList, config?.client_name, single, theme.palette.text.secondary]);

  // Leaving the page drops the per-piece polling of this batch.
  useEffect(() => () => queryClient.removeQueries({ queryKey: [CAMPAIGN_PIECE_QUERY_KEY] }), [queryClient]);

  const plannedPieces = single ? 1 : briefing.frequency * briefing.duration;
  const topicCount = campaign.topics.filter((topic) => String(topic || "").trim()).length;

  // Channels are chosen per piece below its base text, not in the briefing.
  const handleStart = () => {
    if (tutorialCampaign) return undefined;
    const payload = { configId: Number(configId) || configId, ...briefing };
    return single ? campaign.startSingle(payload) : campaign.start(payload);
  };

  let action = {
    label: `Generar ${plannedPieces} ${plannedPieces === 1 ? "pieza" : "piezas"}`,
    onClick: handleStart,
    disabled: !config || campaign.isStarting,
    loading: campaign.isStarting,
    estimate: "Primero revisarás los temas que proponga la IA.",
  };
  if (single) {
    action = {
      label: "Generar contenido",
      onClick: handleStart,
      disabled: action.disabled || !briefing.topic.trim(),
      loading: campaign.isStarting,
    };
    if (started) action = { label: "Contenido en marcha", disabled: true };
  } else if (phase === "topics_loading") {
    action = { label: "Buscando ideas...", disabled: true, loading: true };
  } else if (phase === "topics_review") {
    action = {
      label: campaign.isConfirming ? "Lanzando..." : `Generar ${topicCount} ${topicCount === 1 ? "pieza" : "piezas"}`,
      onClick: campaign.confirmTopics,
      disabled: campaign.isConfirming || !topicCount,
      loading: campaign.isConfirming,
      estimate: minutesLabel(topicCount),
    };
  } else if (phase === "generating") {
    action = { label: "Generando...", disabled: true, loading: true };
  } else if (phase === "done") {
    action = { label: "Textos generados", disabled: true };
  }

  const popupElement = (
    <SuccessPopup
      message={popup.message}
      isOpen={popup.open}
      error={popup.error}
      autoHideDuration={3000}
      onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
    />
  );

  if (!configId || configError) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ ...panelSx, p: 4, maxWidth: 520, mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1.5 }}>
          <Layers size={32} color="#9CA3AF" />
          <Typography sx={{ fontWeight: 800, fontSize: "1.0625rem", color: "#0F172A" }}>
            {configId ? "No se ha podido cargar el cliente" : "Elige un cliente para crear contenido"}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#6B7280" }}>
            El contenido se genera a partir de la configuración de contenidos de un cliente.
          </Typography>
          <Button variant="outline" onClick={goToList}>
            <ArrowLeft size={15} /> Volver a contenidos
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoadingConfig || !config) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tutorialCampaign && isClientReadOnly(user, config.client)) {
    return <ReadOnlyClientNotice clientName={config.client_name} onBack={goToList} />;
  }

  // A single piece is already a piece: its workflow stepper shows from the start.
  const singleSteps = single
    ? getPieceWorkflow(null).steps.map((step) =>
        step.key === "generation" && started ? { ...step, subtitle: "Generando texto base..." } : step
      )
    : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      {popupElement}

      {single && <WorkflowStepper steps={singleSteps} selectedKey="generation" />}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px minmax(0, 1fr)", lg: "360px minmax(0, 1fr)" },
          maxWidth: { md: 1440 }, // ends where the stepper does on wide screens
          gap: 2,
          alignItems: "start",
        }}
      >
        <CampaignBriefingForm
          clientName={config.client_name}
          value={briefing}
          onChange={setBriefing}
          fieldsEditable={!started}
          actionLabel={action.label}
          actionDisabled={action.disabled}
          actionLoading={action.loading}
          onAction={action.onClick}
          estimate={action.estimate}
          single={single}
          clientId={tutorialCampaign ? undefined : config.client}
        />

        <CampaignProgressPanel
          phase={phase}
          plannedPieces={plannedPieces}
          topics={campaign.topics}
          onTopicChange={campaign.setTopic}
          onRemoveTopic={campaign.removeTopic}
          onAddTopic={campaign.addTopic}
          isConfirming={campaign.isConfirming}
          onConfirmTopics={campaign.confirmTopics}
          progress={campaign.progress}
          pieceIds={campaign.pieceIds}
          onOpenPiece={(piece) => navigate(`${LIST_PATH}/pieza/${piece.id}`)}
          onManagePieces={() => {
            if (!tutorialCampaign) navigate(`${LIST_PATH}?config=${configId}`);
          }}
          onSingleReady={(piece) => navigate(`${LIST_PATH}/pieza/${piece.id}`, { replace: true })}
          mockPieces={tutorialCampaign?.pieces}
          mockSelectedChannels={tutorialCampaign?.selectedChannels}
          notify={notify}
          single={single}
        />
      </Box>
    </Box>
  );
}

export default CampaignPage;
